import * as React from 'react'
import useSWR from 'swr'
import axios from 'axios'
import { useDebounce } from 'react-use'
import { withTranslations } from 'helpers'

import ProductSaleItem from '../ProductSaleItem'
import NoProducts from '../selling/NoProducts'
import Paginator from '../atoms/Paginator'
import SellingFilters, {
  SellingFilterState,
  SizeOption,
  FilterFlags,
  EMPTY_FILTERS
} from './SellingFilters'

// The JSON `search` feed returns each row as the presenter's snake_case props
// (the same shape the ERB builds). ProductSaleItem wants camelCase, so we
// camelize the TOP-LEVEL keys only — nested action objects keep their snake_case
// internals (`delete_icon`, `data.method`), which the component expects.
type Hit = Record<string, unknown>

const camelize = (key: string): string =>
  key.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase())

const camelizeTopLevel = (hit: Hit): Hit =>
  Object.fromEntries(
    Object.entries(hit).map(([key, value]) => [camelize(key), value])
  )

type FacetCounts = Record<string, Record<string, number>>

interface SearchResponse {
  hits: Hit[]
  nb_hits: number
  page: number
  nb_pages: number
  facets?: FacetCounts
  available_facets?: string[]
  price_bounds?: { min_cents: number; max_cents: number } | null
  size_options?: SizeOption[]
  filter_flags?: FilterFlags
  sort_options?: string[]
}

// Serialise the filter state into the feed's query params:
//   facet_filters[key][]=value  (OR within a key, AND across keys)
//   price_min_cents / price_max_cents / premium_shippable / in_inventory
// Order the keys deterministically so the SWR cache key is stable for a given
// filter set (Object key order would otherwise vary the string and refetch).
const filtersToParams = (filters: SellingFilterState): string => {
  const params = new URLSearchParams()

  Object.keys(filters.facetFilters)
    .sort()
    .forEach((facetKey) => {
      const values = [...filters.facetFilters[facetKey]].sort()

      values.forEach((value) => {
        params.append(`facet_filters[${facetKey}][]`, value)
      })
    })

  if (filters.priceMinCents != null) {
    params.set('price_min_cents', String(filters.priceMinCents))
  }

  if (filters.priceMaxCents != null) {
    params.set('price_max_cents', String(filters.priceMaxCents))
  }

  // consignment / inventory are tri-state: true, false, or unset. Send the
  // explicit direction (premium_shippable / in_inventory = true|false); omit
  // when unset so the backend doesn't filter on it.
  if (filters.consignment != null) {
    params.set('premium_shippable', String(filters.consignment))
  }

  if (filters.inventory != null) {
    params.set('in_inventory', String(filters.inventory))
  }

  const query = params.toString()

  return query ? `&${query}` : ''
}

const hasActiveFilters = (filters: SellingFilterState): boolean =>
  Object.keys(filters.facetFilters).length > 0 ||
  filters.priceMinCents != null ||
  filters.priceMaxCents != null ||
  filters.consignment != null ||
  filters.inventory != null

const SCALAR_FILTER_PARAMS = [
  'price_min_cents',
  'price_max_cents',
  'premium_shippable',
  'in_inventory'
]

// Hydrate the filter state from the URL so a shared link (or a refresh) lands
// with the same filters applied. Mirrors `filtersToParams`'s param names.
const parseFiltersFromLocation = (): SellingFilterState => {
  const params = new URLSearchParams(window.location.search)
  const facetFilters: Record<string, string[]> = {}

  params.forEach((value, rawKey) => {
    const match = rawKey.match(/^facet_filters\[(.+)\]\[\]$/)

    if (!match) return
    ;(facetFilters[match[1]] ??= []).push(value)
  })

  const min = params.get('price_min_cents')
  const max = params.get('price_max_cents')
  const triState = (value: string | null): boolean | null => {
    if (value === 'true') return true
    if (value === 'false') return false
    return null
  }

  return {
    facetFilters,
    priceMinCents: min != null ? Number(min) : null,
    priceMaxCents: max != null ? Number(max) : null,
    consignment: triState(params.get('premium_shippable')),
    inventory: triState(params.get('in_inventory'))
  }
}

// Reflect the current filters into the address bar (replaceState, no reload) so
// the share button and a browser refresh both carry them.
const writeFiltersToLocation = (filters: SellingFilterState): void => {
  const url = new URL(window.location.href)

  ;[...url.searchParams.keys()].forEach((existing) => {
    if (
      existing.startsWith('facet_filters[') ||
      SCALAR_FILTER_PARAMS.includes(existing)
    ) {
      url.searchParams.delete(existing)
    }
  })
  // filters reset paging to page 1, so drop any stale ?page too
  url.searchParams.delete('page')

  const fragment = filtersToParams(filters).replace(/^&/, '')
  const merged = [url.searchParams.toString(), fragment]
    .filter(Boolean)
    .join('&')

  url.search = merged ? `?${merged}` : ''
  window.history.replaceState({}, '', url.toString())
}

interface Props {
  // GET /account/selling/search.json — the results feed
  searchPath: string
  // current ?search= term so the box + first fetch match the server render
  initialQuery?: string
  // server-rendered first page, used as SWR fallback so there's no load flash
  initialHits?: Hit[]
  // the server's current page + total, so the pager renders without a fetch flash
  initialPage?: number
  initialNbPages?: number
  placeholder?: string
  // admin surfaces (admin selling / admin products) get the premium + inventory
  // toggles; sellers see only a single Consignment filter.
  admin?: boolean
}

const fetcher = (url: string): Promise<SearchResponse> =>
  axios.get<SearchResponse>(url).then((response) => response.data)

const DEBOUNCE_MS = 250
const COPIED_RESET_MS = 2000

const LinkIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

const TickIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const CrossIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const EMPTY_FLAGS: FilterFlags = { consignment: false, inventory: false }

interface FeedState {
  hits: Hit[]
  nbPages: number
  currentPage: number
  settledEmpty: boolean
  facets: FacetCounts
  availableFacets: string[]
  priceBounds: { min_cents: number; max_cents: number } | null
  sizeOptions: SizeOption[]
  filterFlags: FilterFlags
  sortOptions: string[]
}

// Fetch `key` and derive what the view renders. Keeps the last settled response
// on screen while the next is in flight, so changing the term/filters/page never
// blanks the list mid-fetch — `keepPreviousData` covers fetched→fetched, and the
// ref covers the initial server-render → first fetch (no prior FETCHED data for
// SWR to hold). Only a settled, genuinely-empty fetch shows NoProducts.
const useFeed = (
  key: string,
  fallback: SearchResponse | undefined,
  initialHits: Hit[],
  initialNbPages: number,
  page: number
): FeedState => {
  const { data, isValidating } = useSWR<SearchResponse>(key, fetcher, {
    fallbackData: fallback,
    keepPreviousData: true,
    revalidateOnFocus: false
  })

  const lastResponseRef = React.useRef<SearchResponse | null>(null)

  if (data) lastResponseRef.current = data
  const shown = data ?? lastResponseRef.current
  const hits = shown?.hits ?? initialHits

  return {
    hits,
    nbPages: shown?.nb_pages ?? initialNbPages,
    currentPage: shown?.page ?? page,
    settledEmpty: !isValidating && hits.length === 0,
    facets: shown?.facets ?? {},
    availableFacets: shown?.available_facets ?? [],
    priceBounds: shown?.price_bounds ?? null,
    sizeOptions: shown?.size_options ?? [],
    filterFlags: shown?.filter_flags ?? EMPTY_FLAGS,
    sortOptions: shown?.sort_options ?? []
  }
}

const SellingSearch: React.FC<Props> = ({
  searchPath,
  initialQuery,
  initialHits = [],
  initialPage = 1,
  initialNbPages = 1,
  placeholder = 'Search',
  admin = false
}) => {
  // `initialQuery` is the server's ?search= term. With no search it arrives as
  // `null` (a nil `@search_term` serialised by react_component), and the `= ''`
  // default only covers `undefined` — so coerce here before any `.trim()`.
  const safeInitialQuery = initialQuery ?? ''
  const [query, setQuery] = React.useState(safeInitialQuery)
  const [debouncedQuery, setDebouncedQuery] = React.useState(safeInitialQuery)
  const [page, setPage] = React.useState(initialPage)
  const [filters, setFilters] = React.useState<SellingFilterState>(() =>
    typeof window === 'undefined' ? EMPTY_FILTERS : parseFiltersFromLocation()
  )
  const [sort, setSort] = React.useState<string>(() =>
    typeof window === 'undefined'
      ? ''
      : new URLSearchParams(window.location.search).get('sort') ?? ''
  )
  const [copied, setCopied] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  useDebounce(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS, [query])

  // Any change to the term restarts at page 1 (the current page may not exist
  // in the narrowed result set).
  const changeQuery = (value: string) => {
    setQuery(value)
    setPage(1)
  }

  // Same for filters — narrowing can shrink the result set below the current
  // page, so always jump back to page 1 when the filter set changes. Reflect
  // the new set into the URL so the share link + a refresh carry it.
  const changeFilters = (next: SellingFilterState) => {
    setFilters(next)
    setPage(1)
    writeFiltersToLocation(next)
  }

  // Sorting also restarts at page 1; reflect it in the URL (share + refresh).
  const changeSort = (value: string) => {
    setSort(value)
    setPage(1)
    const url = new URL(window.location.href)

    if (value) url.searchParams.set('sort', value)
    else url.searchParams.delete('sort')
    url.searchParams.delete('page')
    window.history.replaceState({}, '', url.toString())
  }

  const clearSearch = () => {
    changeQuery('')
    setDebouncedQuery('')
  }

  // Copy a link that reloads this view with the search pre-applied. Built from
  // the live URL so any params already there (the tab, and — once they land —
  // filters + sort) are carried along; we just overwrite the `search` term.
  const copyShareLink = async () => {
    const url = new URL(window.location.href)
    const trimmed = query.trim()

    if (trimmed) url.searchParams.set('search', trimmed)
    else url.searchParams.delete('search')

    try {
      await navigator.clipboard.writeText(url.toString())
      setCopied(true)
      window.setTimeout(() => setCopied(false), COPIED_RESET_MS)
    } catch {
      // clipboard unavailable (insecure context / denied) — nothing to do
    }
  }

  // The island owns pagination: it fetches page `page` of the feed and renders
  // its own pager (no server reload). The server render seeds the first page so
  // there's no fetch flash on load.
  const separator = searchPath.includes('?') ? '&' : '?'
  const sortParam = sort ? `&sort=${sort}` : ''
  const key = `${searchPath}${separator}query=${encodeURIComponent(
    debouncedQuery
  )}&page=${page}${filtersToParams(filters)}${sortParam}`

  // The server render only seeds page 1 of the un-narrowed list, so it's only a
  // valid SWR fallback while the term, page AND filters are all still at their
  // initial (empty) state — any filter means we must fetch.
  const isServerRendered =
    page === initialPage &&
    debouncedQuery === safeInitialQuery.trim() &&
    !hasActiveFilters(filters) &&
    !sort

  const fallback: SearchResponse | undefined = isServerRendered
    ? {
        hits: initialHits,
        nb_hits: initialHits.length,
        page: initialPage,
        nb_pages: initialNbPages
      }
    : undefined

  const {
    hits,
    nbPages,
    currentPage,
    settledEmpty,
    facets,
    availableFacets,
    priceBounds,
    sizeOptions,
    filterFlags,
    sortOptions
  } = useFeed(key, fallback, initialHits, initialNbPages, page)

  const goToPage = (next: number) => {
    setPage(next)
    // keep ?page in the URL so a refresh lands on the same page
    const url = new URL(window.location.href)

    url.searchParams.set('page', String(next))
    window.history.replaceState({}, '', url.toString())
    // bring the top of the results into view — not the very top of the page
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div
      className="selling-search"
      data-testid="selling-search"
      ref={containerRef}
    >
      {/* Mirror the server `_search_form` markup so it inherits the same styles:
          `.field--search` gives the icon its positioning context and
          `.input--search` the rounded pill + left padding for the icon. */}
      <div className="list-item__search">
        <form
          role="search"
          onSubmit={(event) => {
            // live search already runs on the debounce; submitting (Enter or the
            // icon) just flushes it so there's no wait for an explicit search
            event.preventDefault()
            setDebouncedQuery(query.trim())
          }}
        >
          <div className="field field--search field--search--controls">
            <input
              type="text"
              id="search"
              name="search"
              autoComplete="off"
              data-cs-encrypt
              className="input--search"
              value={query}
              placeholder={placeholder}
              aria-label={placeholder}
              onChange={(event) => changeQuery(event.target.value)}
            />
            <button
              className="btn--search icon-search"
              type="submit"
              aria-label={placeholder}
            />
            <button
              type="button"
              className={`search-controls__share${
                query ? ' search-controls__share--with-clear' : ''
              }`}
              onClick={copyShareLink}
              aria-label="Copy a shareable link to this search"
              title={copied ? 'Link copied' : 'Copy a shareable link'}
            >
              {copied ? <TickIcon /> : <LinkIcon />}
            </button>
            {query && (
              <button
                type="button"
                className="search-controls__clear"
                onClick={clearSearch}
                aria-label="Clear search"
                title="Clear search"
              >
                <CrossIcon />
              </button>
            )}
          </div>
        </form>
      </div>
      <span
        className="search-controls__sr-status"
        role="status"
        aria-live="polite"
      >
        {copied ? 'Link copied' : ''}
      </span>

      <SellingFilters
        admin={admin}
        facets={facets}
        availableFacets={availableFacets}
        priceBounds={priceBounds}
        sizeOptions={sizeOptions}
        filterFlags={filterFlags}
        sortOptions={sortOptions}
        sort={sort}
        onSortChange={changeSort}
        filters={filters}
        onChange={changeFilters}
      />

      {settledEmpty && <NoProducts />}

      {hits.map((hit) => {
        const { id, ...rest } = camelizeTopLevel(hit)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return <ProductSaleItem key={String(id)} {...(rest as any)} />
      })}

      {nbPages > 1 && (
        <Paginator
          page={currentPage}
          totalPages={nbPages}
          window={2}
          onPageChange={goToPage}
        />
      )}
    </div>
  )
}

// Wrapped here rather than on SellingFilters: this is the component Rails
// mounts, so the translation context has to be provided at this level for the
// children to read it.
export default withTranslations(SellingSearch)
